import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import type {
  User,
  Workspace,
  Asset,
  Account,
  Snapshot,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Pagination,
  ApiResponse,
  ApiToken,
  ApiTokenCreate,
  ApiTokenUpdate,
  ApiTokenCreateResponse
} from '@/types'

// TypeScript declaration for import.meta.env
declare global {
  interface ImportMeta {
    env: Record<string, string | undefined>
  }
}

// 获取store实例的辅助函数（延迟加载避免循环依赖）
let _authStoreInstance: any = null
async function getAuthStoreAsync(): Promise<any | null> {
  if (_authStoreInstance) {
    return _authStoreInstance
  }
  try {
    // 动态导入store（避免循环依赖）
    const { useAuthStore } = await import('@/store/auth')
    _authStoreInstance = useAuthStore()
    return _authStoreInstance
  } catch (e) {
    console.warn('Failed to get auth store:', e)
    return null
  }
}

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json'
      }
    })

    // 请求拦截器
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token')
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // 如果后端返回的是 {code, message, data} 格式，直接返回 data
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          return response.data.data || response.data
        }
        return response.data
      },
      async (error) => {
        if (error.response?.status === 401) {
          // 清理本地状态的辅助函数（异步）
          const clearAuthState = async () => {
            try {
              const store = await getAuthStoreAsync()
              if (store) {
                // 直接清理store状态（不调用logout接口，避免循环）
                store.user = null
                store.accessToken = null
                store.refreshToken = null
              }
              // 清理workspace数据
              try {
                const workspaceModule = await import('@/store/workspace')
                const workspaceStore = workspaceModule.useWorkspaceStore()
                workspaceStore.clearWorkspaceData()
              } catch (e) {
                // 忽略workspace清理错误
                console.warn('Failed to clear workspace store:', e)
              }
            } catch (e) {
              // 如果store不可用，只清理localStorage
              console.warn('Failed to clear auth store:', e)
            }
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('user_info')
            localStorage.removeItem('current_workspace_id')
          }

          // 跳过刷新token接口本身的401错误，避免循环
          if (error.config?.url?.includes('/auth/refresh')) {
            // 刷新token失败，清理状态并跳转到登录页
            await clearAuthState()
            window.location.href = '/login'
            return Promise.reject(error)
          }

          // 检查是否已经有重试标记，避免无限重试
          const retryKey = `__retry_${error.config?.url}`
          if ((error.config as any)?.[retryKey]) {
            // 已经重试过了，直接清理状态并跳转
            await clearAuthState()
            window.location.href = '/login'
            return Promise.reject(error)
          }

          // Token过期，尝试刷新
          const refreshToken = localStorage.getItem('refresh_token')
          if (refreshToken) {
            try {
              const response = await this.refreshToken(refreshToken)
              localStorage.setItem('access_token', response.access_token)
              // 更新store中的token
              try {
                const store = await getAuthStoreAsync()
                if (store) {
                  store.accessToken = response.access_token
                }
              } catch (e) {
                // 忽略store更新错误
              }
              
              // 标记已重试，重新发送原请求
              if (error.config) {
                (error.config as any)[retryKey] = true
                if (error.config.headers) {
                  error.config.headers.Authorization = `Bearer ${response.access_token}`
                }
                return this.client.request(error.config)
              }
            } catch (refreshError) {
              // 刷新失败，清理状态并跳转到登录页
              await clearAuthState()
              window.location.href = '/login'
              return Promise.reject(refreshError)
            }
          } else {
            // 没有刷新Token，清理状态并跳转到登录页
            await clearAuthState()
            window.location.href = '/login'
          }
        }
        return Promise.reject(error)
      }
    )
  }

  // 认证相关
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await this.client.post<AuthResponse>('/auth/login', data)
    return response as any
  }

  async register(data: RegisterRequest): Promise<User> {
    const response = await this.client.post<User>('/auth/register', data)
    return response as any
  }

  async refreshToken(refreshToken: string) {
    const response = await this.client.post('/auth/refresh', { refresh_token: refreshToken })
    return response as any
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout')
  }

  // 工作空间相关
  async getWorkspaces(): Promise<Workspace[]> {
    const response = await this.client.get<ApiResponse<{ workspaces: Workspace[] }>>('/workspaces')
    return (response as any).workspaces || []
  }

  async createWorkspace(data: { name: string; description?: string; icon_url?: string; is_default?: boolean }): Promise<Workspace> {
    const response = await this.client.post<Workspace>('/workspaces', data)
    return response as any
  }

  async updateWorkspace(id: number, data: Partial<Workspace>): Promise<Workspace> {
    const response = await this.client.put<Workspace>(`/workspaces/${id}`, data)
    return response as any
  }

  async deleteWorkspace(id: number): Promise<void> {
    await this.client.delete(`/workspaces/${id}`)
  }

  // 基准货币相关
  async getWorkspaceBaseCurrency(workspaceId: number): Promise<{ base_currency: string; base_currency_updated_at?: string }> {
    const response = await this.client.get(`/workspaces/${workspaceId}/base-currency`)
    return response as any
  }

  async updateWorkspaceBaseCurrency(workspaceId: number, baseCurrency: string): Promise<{ base_currency: string; base_currency_updated_at?: string }> {
    const response = await this.client.put(`/workspaces/${workspaceId}/base-currency`, { base_currency: baseCurrency })
    return response as any
  }

  // 账号相关
  async getAccounts(workspaceId: number): Promise<Account[]> {
    const response = await this.client.get(`/accounts?workspace_id=${workspaceId}`)
    const data = response as any
    return Array.isArray(data) ? data : (data.accounts || [])
  }

  async createAccount(workspaceId: number, data: { name: string; type: string; description?: string; is_active?: boolean }): Promise<Account> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.post<Account>(`/accounts?${queryParams.toString()}`, data)
    return response as any
  }

  async getAccount(workspaceId: number, accountId: number): Promise<Account> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.get<Account>(`/accounts/${accountId}?${queryParams.toString()}`)
    return response as any
  }

  async updateAccount(workspaceId: number, accountId: number, data: Partial<Account>): Promise<Account> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.put<Account>(`/accounts/${accountId}?${queryParams.toString()}`, data)
    return response as any
  }

  async deleteAccount(workspaceId: number, accountId: number): Promise<void> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    await this.client.delete(`/accounts/${accountId}?${queryParams.toString()}`)
  }

  // 资产相关
  async getAssets(
    workspaceId: number,
    params?: {
      page?: number
      size?: number
      type?: string
      account_id?: number
      currency?: string
      is_active?: boolean
      search?: string
    }
  ): Promise<{ assets: Asset[]; pagination: Pagination; summary: any }> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString())
        }
      })
    }
    const response = await this.client.get(`/assets?${queryParams.toString()}`)
    return response as any
  }

  async createAsset(workspaceId: number, data: Partial<Asset>): Promise<Asset> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.post<Asset>(`/assets?${queryParams.toString()}`, data)
    return response as any
  }

  async getAsset(workspaceId: number, assetId: number): Promise<Asset> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.get<Asset>(`/assets/${assetId}?${queryParams.toString()}`)
    return response as any
  }

  async updateAsset(workspaceId: number, assetId: number, data: Partial<Asset>): Promise<Asset> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.put<Asset>(`/assets/${assetId}?${queryParams.toString()}`, data)
    return response as any
  }

  async deleteAsset(workspaceId: number, assetId: number): Promise<void> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    await this.client.delete(`/assets/${assetId}?${queryParams.toString()}`)
  }

  // 快照相关
  async getSnapshots(
    workspaceId: number,
    params?: { page?: number; size?: number; type?: string }
  ): Promise<{ snapshots: Snapshot[]; pagination: Pagination }> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString())
        }
      })
    }
    const response = await this.client.get(`/snapshots?${queryParams.toString()}`)
    return response as any
  }

  async createSnapshot(workspaceId: number, data: { type: string; notes?: string; snapshot_date?: string }): Promise<Snapshot> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.post<Snapshot>(`/snapshots?${queryParams.toString()}`, data)
    return response as any
  }

  async deleteSnapshot(workspaceId: number, snapshotId: number): Promise<void> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    await this.client.delete(`/snapshots/${snapshotId}?${queryParams.toString()}`)
  }

  // 资产统计相关
  async getAssetsByType(workspaceId: number): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.get(`/assets/statistics/by-type?${queryParams.toString()}`)
    return response as any
  }

  async getAssetsByAccount(workspaceId: number): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.get(`/assets/statistics/by-account?${queryParams.toString()}`)
    return response as any
  }

  async getLiabilitiesByType(workspaceId: number): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.get(`/assets/statistics/liabilities-by-type?${queryParams.toString()}`)
    return response as any
  }

  // 市场数据相关
  async getLatestCurrencyRates(baseCurrency?: string, limit?: number): Promise<any> {
    const queryParams = new URLSearchParams()
    if (baseCurrency) queryParams.append('base_currency', baseCurrency)
    if (limit) queryParams.append('limit', limit.toString())
    const response = await this.client.get(`/market-data/currency-rates?${queryParams.toString()}`)
    return response as any
  }

  async getLatestStockPrices(workspaceId?: number, limit?: number, offset?: number): Promise<any> {
    const queryParams = new URLSearchParams()
    if (workspaceId) queryParams.append('workspace_id', workspaceId.toString())
    if (limit) queryParams.append('limit', limit.toString())
    if (offset) queryParams.append('offset', offset.toString())
    const response = await this.client.get(`/market-data/stock-prices?${queryParams.toString()}`)
    return response as any
  }

  async getLatestFundPrices(workspaceId?: number, limit?: number, offset?: number): Promise<any> {
    const queryParams = new URLSearchParams()
    if (workspaceId) queryParams.append('workspace_id', workspaceId.toString())
    if (limit) queryParams.append('limit', limit.toString())
    if (offset) queryParams.append('offset', offset.toString())
    const response = await this.client.get(`/market-data/fund-prices?${queryParams.toString()}`)
    return response as any
  }

  async validateFund(fundCode: string, fundType: string = 'ETF', force: boolean = false): Promise<{
    valid: boolean | null
    fund_info?: {
      fund_code: string
      fund_name: string
      fund_type: string
      price: number
    }
    from_cache?: boolean
    need_confirm?: boolean
    hint?: string
  }> {
    const queryParams = new URLSearchParams()
    queryParams.append('fund_code', fundCode)
    queryParams.append('fund_type', fundType)
    if (force) queryParams.append('force', 'true')
    const response = await this.client.get(`/market-data/validate-fund?${queryParams.toString()}`)
    return response as any
  }

  async updateMarketData(dataType: 'currency' | 'stock' | 'all', force: boolean = false): Promise<any> {
    // 市场数据更新可能需要较长时间，设置30秒超时
    const response = await this.client.post('/market-data/update', {
      data_type: dataType,
      force: force
    }, {
      timeout: 30000  // 30秒超时
    })
    return response as any
  }

  async updateFundPrices(force: boolean = false): Promise<any> {
    const response = await this.client.post('/market-data/update-fund-prices', null, {
      params: { force },
      timeout: 30000  // 30秒超时
    })
    return response as any
  }

  // 货币相关
  async getSupportedCurrencies(): Promise<{ currencies: any[]; total: number }> {
    const response = await this.client.get('/currency/supported')
    return response as any
  }

  async getExchangeRates(baseCurrency: string = 'CNY'): Promise<any[]> {
    const response = await this.client.get(`/currency/rates?base_currency=${baseCurrency}`)
    return response as any
  }

  async refreshExchangeRates(baseCurrency: string = 'CNY', force: boolean = false): Promise<any> {
    const response = await this.client.post('/currency/rates/refresh', {
      base_currency: baseCurrency,
      force
    })
    return response as any
  }

  async getCurrencyInfo(currencyCode: string): Promise<any> {
    const response = await this.client.get(`/currency/info/${currencyCode}`)
    return response as any
  }

  // 快照分析相关（新增）
  async getSnapshotTrend(
    workspaceId: number,
    params: {
      dimension_type: 'account' | 'asset_type' | 'currency'
      start_date?: string
      end_date?: string
      days?: number
      target_currency?: string
    }
  ): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    queryParams.append('dimension_type', params.dimension_type)
    if (params.start_date) queryParams.append('start_date', params.start_date)
    if (params.end_date) queryParams.append('end_date', params.end_date)
    if (params.days) queryParams.append('days', params.days.toString())
    if (params.target_currency) queryParams.append('target_currency', params.target_currency)
    
    const response = await this.client.get(`/snapshots/analytics/trend?${queryParams.toString()}`)
    return response as any
  }

  async getSnapshotDetailed(workspaceId: number, snapshotId: number): Promise<any> {
    const queryParams = new URLSearchParams()
    queryParams.append('workspace_id', workspaceId.toString())
    const response = await this.client.get(`/snapshots/${snapshotId}/detailed?${queryParams.toString()}`)
    return response as any
  }

  // Token 相关
  async getTokens(): Promise<ApiToken[]> {
    const response = await this.client.get('/tokens')
    const data = response as any
    return Array.isArray(data) ? data : (data.tokens || [])
  }

  async createToken(data: ApiTokenCreate): Promise<ApiTokenCreateResponse> {
    const response = await this.client.post('/tokens', data)
    return response as any
  }

  async getToken(tokenId: number): Promise<ApiToken> {
    const response = await this.client.get(`/tokens/${tokenId}`)
    return response as any
  }

  async updateToken(tokenId: number, data: ApiTokenUpdate): Promise<ApiToken> {
    const response = await this.client.put(`/tokens/${tokenId}`, data)
    return response as any
  }

  async deleteToken(tokenId: number): Promise<void> {
    await this.client.delete(`/tokens/${tokenId}`)
  }
}

export const api = new ApiClient()
